import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Code, Brain, GitBranch, Users, Play, Database, Workflow } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function GitHubLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Install Memara GitHub App
Visit GitHub App marketplace

# 2. Configure repository access
Grant access to repositories you want to track

# 3. Set up memory rules
Configure what code events create memories

# 4. Start capturing development memories
Commits, PRs, and issues become searchable memories`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="github"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-900 shadow" aria-label="GitHub Logo">
          <Icon icon="logos:github-icon" width={48} height={48} />
        </span>
      }
      title="GitHub Integration"
      description="Enterprise development memory for code repositories. Automatically capture commits, pull requests, issues, and code discussions to build searchable development knowledge with intelligent context understanding and team collaboration features."
      features={[
        { icon: <Code className="h-4 w-4 text-gray-900" />, title: 'Code Repository Memory', description: 'Capture commits, PRs, issues, and code discussions with intelligent context extraction and semantic understanding.' },
        { icon: <Brain className="h-4 w-4 text-gray-900" />, title: 'Development Intelligence', description: 'AI-powered analysis of code patterns, development workflows, and technical decision context across repositories.' },
        { icon: <GitBranch className="h-4 w-4 text-gray-900" />, title: 'Multi-Repository Tracking', description: 'Comprehensive development progress tracking across all repositories with branch and merge context.' },
        { icon: <Users className="h-4 w-4 text-gray-900" />, title: 'Team Development Memory', description: 'Shared development knowledge base with role-based access controls and collaborative code insights.' },
        { icon: <Database className="h-4 w-4 text-gray-900" />, title: 'Technical Documentation', description: 'Automated capture of technical discussions, architectural decisions, and development best practices.' },
        { icon: <Workflow className="h-4 w-4 text-gray-900" />, title: 'CI/CD Integration', description: 'Memory capture from GitHub Actions, deployment workflows, and automated development processes.' },
      ]}
      setupSteps={[
        'Install Memara GitHub App from marketplace',
        'Configure repository access and permissions',
        'Set up intelligent memory capture rules',
        'Start automated development knowledge capture',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-gray-300 text-gray-900 hover:bg-gray-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-gray-300 text-gray-900 hover:bg-gray-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Icon icon="logos:github-icon" width={20} height={20} />,
        },
      ]}
      videoUrl="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
      microcopy="Trusted by software engineering teams and enterprise development organizations using GitHub for code collaboration and version control."
      trustedBy="GitHub, Enterprise Development Teams, and more."
      testimonial="Memara's GitHub integration transforms our development workflow into an intelligent knowledge system that captures and contextualizes every important code decision and technical discussion."
    />
  );
} 